import React, { Component, lazy, Suspense } from 'react';
import timeLimit from 'time-limit-promise';
import captureException from './utils/sentry';
import { Boundary } from './components/ErrorBoundary';
import { logError, logInfo } from './shared/utils/logging';
import {
  CandyRackContextConsumer,
  CandyRackContextProvider,
} from './contexts/CandyRackContext';
import {
  ProductsContextConsumer,
  ProductsContextProvider,
} from './contexts/ProductsContext';
import { CurrencyContextProvider } from './contexts/CurrencyContext';
import {
  removeCandyRackAddToCartDiscount,
  updateDiscounts,
} from './shared/utils/discounts';
import {
  ASSOCIATED_PRODUCTS_FOR_REMOVAL_KEY,
  getAssociatedProducts,
  getAssociatedProductsForRemoval,
  saveAssociatedProduct,
  saveTranslations,
} from './shared/utils/cart';
import { chooseOfferForABTest } from './utils/offer-utils';
import { hasExceededViewsPerSession } from './utils/views-per-session';
import {
  AddButtonAction,
  AddToCartButtonClickedCustomEventDetail,
  AppInfoCandyRack,
  ButtonAction,
  CandyRackSettings,
  CandyRackText,
  Offer,
  OffersResponseBody,
  Product,
} from './types';
import { SharedContextProvider } from './contexts/SharedContext';
import { getOffers, productHandleForVariant } from './shared/api/candyrack';
import {
  addToCart,
  getProduct,
  removeFromCart,
} from './shared/api/shared-app-requests';
import reportChunkError from './dissolve/services/chunkErrorMonitoring';
import { REGISTERED_APPS } from './shared/types/Apps';
import { setMoneyFormats } from './utils/popup-utils';
import storage from './shared/product-page-placement/storage';

import { CANDYRACK_ASSOCIATED_PRODUCTS_LOCAL_STORAGE_KEY } from './shared/constants/app';
import toggleBodyStyle from './utils/toggleBodyStyle';
import {
  clickHiddenButton,
  getCustomProperties,
  getDefaultProductVariantId,
  getSelectedVariantIdFromURL,
  productIsMissingTheVariant,
  quantityForButton,
  redirect,
  stayOnProductPage,
  variantIdForButton,
  validateForm,
} from './utils/candyRackAppUtils';
import {
  isProductPageExtensionStoreButtonActive,
  loadProductPageExtensionForCheckboxLayoutStoreButton,
} from './shared/product-page-placement/add-to-cart-ppp';
import { PRODUCT_PAGE_POPUP_TRANSLATIONS_KEY } from './graphql/constants/index';
import getTranslationsMetaobject from './graphql/utils/getTranslationsMetaobject';
import { getTranslatedOffersForCandyRack } from './graphql/utils/getTranslatedOffers';
import {
  trackAddToCart,
  trackRemoveFromCart,
} from './utils/tracking-utils-candyrack';
import { ShopifyAddToCartResponse } from './shared/api/types';
import { shouldTrackAddToCart } from './shared/api/utils';
import { isFunction } from './shared/product-page-placement/update-drawer-cart';
import { initializeIntelligemsTestFacade } from './shared/test-facade/intelligemsTestFacade';
import { logIntelligemsStatus } from './shared/product-page-placement/intelligemsUtils';
import { AppsInfoProvider } from './contexts/AppsInfoContext';
import { applyIntelligemsFilterForAddToCart } from './shared/product-page-placement/intelligemsOfferFilterUtils';
import applyDiscounts from './graphql/utils/applyDiscounts';

const Popup = lazy(() =>
  import(/* webpackPrefetch: true */ './components/Popup').catch(() =>
    reportChunkError(REGISTERED_APPS.ADD_TO_CART, window.Shopify.shop),
  ),
);

interface AppState {
  button: HTMLElement;
  modalOpen: boolean;
  offers: Offer[];
  settings: CandyRackSettings;
  currency: string;
  rejectInProgress: boolean;
}

interface Props {
  productHandle: string;
  appsInfo: AppInfoCandyRack;
  useDSRecommEngine: boolean;
}

interface AppProps extends Props {
  removeOffersWithUnavailableProducts: (offers: Offer[]) => Promise<Offer[]>;
  removeOffersWithProductAlreadyInCart: (offers: Offer[]) => Promise<Offer[]>;
  parentProduct?: Product;
  setParentProduct: (product: Product) => void;
  parentProductVariantId?: number;
  setParentProductVariantId: (id: number) => void;
  resetParentProductVariantId: () => void;
  parentQuantity: number;
  upgradeOffer?: Offer;
  setUpgradeOffer: (offer: Offer) => void;
  resetUpgradeOffer: () => void;
  setParentQuantity: (quantity: number) => void;
}

class App extends Component<AppProps, AppState> {
  rejectInProgress: boolean = false;

  handleExternalAddToCartButtonClick = async ({
    detail: { clonedButton, hiddenButton, variantId },
  }: CustomEvent<AddToCartButtonClickedCustomEventDetail>) => {
    const { setParentProduct, setParentProductVariantId, setParentQuantity } =
      this.props;
    window.CandyRack.buttonState = 'handling';

    // Adding products from Product Page Placement (store button)
    if (isProductPageExtensionStoreButtonActive()) {
      await loadProductPageExtensionForCheckboxLayoutStoreButton(
        this.props.appsInfo.storefront_access_token,
      );
    }

    if (window.CandyRack?.popupOpen || !validateForm(clonedButton)) {
      clickHiddenButton(hiddenButton);
      return;
    }

    // Retrieve preloaded product
    let product = await timeLimit(this.getProductPromise, 1000);

    // Make sure the preloaded product includes variant ID present in current form
    if (await productIsMissingTheVariant(product, clonedButton)) {
      product = null;
    }

    // Partners can send variantId through the event
    const parentProductVariantId =
      variantId ||
      (await variantIdForButton(clonedButton)) ||
      getSelectedVariantIdFromURL() ||
      getDefaultProductVariantId(product);

    if (!parentProductVariantId) {
      logInfo('parentProductVariantId not found', REGISTERED_APPS.ADD_TO_CART);
    } else {
      setParentProductVariantId(parentProductVariantId);
    }

    if (!product) {
      try {
        const handle = await productHandleForVariant(parentProductVariantId);
        if (handle) {
          product = await getProduct(
            handle,
            this.props.appsInfo.storefront_access_token,
          );
        }
        if (!product) {
          throw Error("Couldn't find product info.");
        }
      } catch (error) {
        logError(
          'Error while getting product info from variant ID',
          REGISTERED_APPS.ADD_TO_CART,
          error,
        );
        clickHiddenButton(hiddenButton);
        return;
      }
    }

    setParentProduct(product);

    const quantity = quantityForButton(clonedButton);
    setParentQuantity(quantity);

    const offersResponse = await timeLimit(
      this._getOffers(product, quantity, parentProductVariantId),
      4000,
    );

    if (!offersResponse) {
      logInfo('Uninstalled', REGISTERED_APPS.ADD_TO_CART);
      clickHiddenButton(hiddenButton);
      return;
    }

    const { offers, settings } = offersResponse;

    if (!offers || offers.length === 0) {
      logInfo('There are no offers active.', REGISTERED_APPS.ADD_TO_CART);
      clickHiddenButton(hiddenButton);
      return;
    }

    if (hasExceededViewsPerSession(settings.max_popup_session_views)) {
      logInfo('Exceeded views per session.', REGISTERED_APPS.ADD_TO_CART);
      clickHiddenButton(hiddenButton);
      return;
    }

    window.CandyRack.popupOpen = true;
    this.setState({
      modalOpen: true,
      button: hiddenButton,
    });
    if (!window.CANDYRACK_DISABLE_BODY_STYLES) {
      toggleBodyStyle(true);
    }
  };

  getProductPromise: Promise<any> = Promise.resolve(null);

  componentDidMount() {
    // Initialize test facade for QA Robot Framework integration tests
    initializeIntelligemsTestFacade(REGISTERED_APPS.ADD_TO_CART);

    // Check Intelligems status
    logIntelligemsStatus(
      this.props.appsInfo.is_intelligems_enabled,
      REGISTERED_APPS.ADD_TO_CART,
    );

    const { productHandle } = this.props;
    this.attachEventListener();
    this.getProductPromise = this.getProduct(productHandle);
    logInfo('mounted 🎉', REGISTERED_APPS.ADD_TO_CART);
    window.CandyRack.isLoading = false;
    document.dispatchEvent(new CustomEvent('candyrack-mounted'));
  }

  attachEventListener = () => {
    document.addEventListener(
      'addToCartButtonClicked',
      this.handleExternalAddToCartButtonClick as unknown as EventListener,
    );
  };

  componentWillUnmount() {
    document.removeEventListener(
      'addToCartButtonClicked',
      this.handleExternalAddToCartButtonClick as unknown as EventListener,
    );
  }

  getProduct = async (handle: string) => {
    if (!handle) {
      return;
    }

    try {
      return await getProduct(
        handle,
        this.props.appsInfo.storefront_access_token,
      );
    } catch (error) {
      logError(
        'Error while getting product',
        REGISTERED_APPS.ADD_TO_CART,
        error,
      );
      captureException(error);
    }
  };

  _getOffers = async (
    product: Product,
    quantity: number,
    parentProductVariantId?: number,
  ) => {
    const {
      removeOffersWithUnavailableProducts,
      removeOffersWithProductAlreadyInCart,
    } = this.props;
    if (!product) {
      return;
    }
    try {
      const offersResponseFromBE = await getOffers(
        product,
        parentProductVariantId,
      );
      if (!offersResponseFromBE) {
        logInfo('was uninstalled.', REGISTERED_APPS.ADD_TO_CART);
        return;
      }

      const filteredOffersResponse: OffersResponseBody = {
        ...offersResponseFromBE,
        offers: applyIntelligemsFilterForAddToCart(
          offersResponseFromBE.offers,
          this.props.appsInfo.is_intelligems_enabled,
        ),
      };

      filteredOffersResponse.offers.forEach((offer: Offer) => {
        offer.maxQuantityComputedInFE = offer.match_product_quantity
          ? quantity
          : 1;
        offer.parentProduct = product;
      });

      setMoneyFormats(
        filteredOffersResponse.money_formats,
        filteredOffersResponse.currency !== window.Shopify?.currency?.active,
        REGISTERED_APPS.ADD_TO_CART,
      );

      const {
        offers: offersFromResponse,
        money_formats,
        ab_test_id,
        ...response
      } = {
        ...filteredOffersResponse,
      };

      const translationsMetaobjectData = await getTranslationsMetaobject(
        this.props.appsInfo.storefront_access_token,
        PRODUCT_PAGE_POPUP_TRANSLATIONS_KEY,
      );

      // If translations are fetched, we use them, otherwise we default to fallback
      if (translationsMetaobjectData) {
        const {
          add,
          added,
          back,
          subtitle,
          upgrade,
          upgraded,
          cart_discount_note,
          continue_to,
          title,
          reject,
        } = translationsMetaobjectData as unknown as CandyRackText;

        // Setting translated strings, in case of empty string, we also display fallback
        const settingsWithUpdatedTranslations: CandyRackSettings = {
          ...(response.settings as CandyRackSettings),
          text: {
            add: add || response.settings.text.add,
            added: added || response.settings.text.added,
            back: back || response.settings.text.back,
            subtitle: subtitle || response.settings.text.subtitle,
            upgrade:
              upgrade || (response.settings as CandyRackSettings).text.upgrade,
            upgraded:
              upgraded ||
              (response.settings as CandyRackSettings).text.upgraded,
            cart_discount_note:
              cart_discount_note ||
              (response.settings as CandyRackSettings).text.cart_discount_note,
            continue_to:
              continue_to ||
              (response.settings as CandyRackSettings).text.continue_to,
            title: title || (response.settings as CandyRackSettings).text.title,
            reject:
              reject || (response.settings as CandyRackSettings).text.reject,
          },
        };

        // Setting the state and discount note for the translated strings
        this.setState({
          ...response,
          settings: settingsWithUpdatedTranslations,
        });
        saveTranslations(settingsWithUpdatedTranslations);
      } else {
        this.setState({
          ...response,
          settings: response.settings as CandyRackSettings,
        });
        saveTranslations(filteredOffersResponse.settings);
      }

      // Translating offer strings - returns offers in case of no translations
      const offersTranslated = await getTranslatedOffersForCandyRack(
        offersFromResponse,
        this.props.appsInfo.storefront_access_token,
      );

      removeOffersWithUnavailableProducts(offersTranslated).then((offers) => {
        removeOffersWithProductAlreadyInCart(offers).then((filteredOffers) => {
          try {
            if (ab_test_id && filteredOffers && filteredOffers.length > 0) {
              const selectedOffer = chooseOfferForABTest(
                ab_test_id,
                filteredOffers,
              );
              this.setState({ offers: [selectedOffer] });
            } else {
              this.setState({ offers: filteredOffers });
            }
          } catch (error) {
            // since ABTest is PoC let's be super safe, catch all errors and act as there is no ab test in case error occurs
            this.setState({ offers: filteredOffers });
            logError(
              'Error while processing ABTest logic',
              REGISTERED_APPS.ADD_TO_CART,
              error,
            );
            captureException(error);
          }

          if (
            window.CandyRack.popupOpen &&
            (!filteredOffers || filteredOffers.length === 0)
          ) {
            logInfo(
              'is closing because no products are available.',
              REGISTERED_APPS.ADD_TO_CART,
            );
            clickHiddenButton(this.state.button);
            window.CandyRack.popupOpen = false;
            this.setState({ modalOpen: false });
            toggleBodyStyle(false);
          }
        });
      });
      return filteredOffersResponse;
    } catch (error) {
      logError(
        'Error while getting offers',
        REGISTERED_APPS.ADD_TO_CART,
        error,
      );
      captureException(error);
    }
  };

  _handleAction = async (action: ButtonAction) => {
    const { parentProductVariantId, parentQuantity } = this.props;
    window.CandyRack.buttonState = 'idle';
    try {
      const properties = getCustomProperties(this.state.button);
      const response = await addToCart(
        parentProductVariantId!,
        parentQuantity,
        properties,
      );

      if (response.status === 422) {
        logInfo(response.description, REGISTERED_APPS.ADD_TO_CART);
      }
      if (action === 'stay_at_product_page') {
        stayOnProductPage(this.state.button);
      } else if (
        action === 'redirect_to_checkout' ||
        action === 'redirect_to_cart'
      ) {
        redirect(action);
      }
      return;
    } catch (error) {
      captureException(error);
    }
  };

  upgradeOffer = async (upgradeOffer: Offer, action: ButtonAction) => {
    const {
      shopifyProductVariantIdComputedInFE,
      quantityComputedInFE,
      noteComputedInFE,
    } = upgradeOffer;

    const isModifiedRequest = isFunction(
      window.CANDYRACK_MODIFY_UPGRADE_ADD_TO_CART_BODY,
    );

    const response = await addToCart(
      shopifyProductVariantIdComputedInFE,
      quantityComputedInFE,
      noteComputedInFE.trim().length ? { note: noteComputedInFE } : null,
      isModifiedRequest,
    );

    // handles upgrade offers logic + remove upsell when parent is removed
    const upgradeOfferParentProductHandle = upgradeOffer?.parentProduct?.handle;
    const upgradeOfferProductHandleComputed =
      upgradeOffer?.shopifyProductHandleComputedInFE;
    const upgradeOfferProductVariantId =
      upgradeOffer?.shopifyProductVariantIdComputedInFE;

    const products = getAssociatedProducts();
    for (const variantId in products) {
      if (products[variantId] === upgradeOfferParentProductHandle) {
        products[variantId] = upgradeOfferProductHandleComputed;
      }
    }

    const productsForRemoval = getAssociatedProductsForRemoval();
    for (const variantId in productsForRemoval) {
      if (
        productsForRemoval[variantId].handle === upgradeOfferParentProductHandle
      ) {
        productsForRemoval[variantId].handle =
          upgradeOfferProductHandleComputed;
        productsForRemoval[variantId].parentProductVariantId =
          upgradeOfferProductVariantId;
      }
    }

    storage.setItem(
      CANDYRACK_ASSOCIATED_PRODUCTS_LOCAL_STORAGE_KEY,
      JSON.stringify(products),
    );
    storage.setItem(
      ASSOCIATED_PRODUCTS_FOR_REMOVAL_KEY,
      JSON.stringify(productsForRemoval),
    );

    if (response.status === 422) {
      logInfo(response.description, REGISTERED_APPS.ADD_TO_CART);
      this.handleClose(true, action);
    }

    // Discount needs to be applied after upgrade is added to the cart
    if (upgradeOffer.apply_discount) {
      await applyDiscounts({
        token: this.props.appsInfo.storefront_access_token,
        discountCodes: [upgradeOffer.discount_code],
      });
    }

    // Only track if add to cart was completely successful (no status field = success)
    const addedSuccessfully = shouldTrackAddToCart(
      response as ShopifyAddToCartResponse,
    );
    if (addedSuccessfully) {
      await trackAddToCart(upgradeOffer, this.props.parentProduct!);
    }

    window.CandyRack.popupOpen = false;
    this.setState({ modalOpen: false });

    if (
      window.CANDYRACK_CUSTOM_UPGRADE_HANDLE ||
      action === 'stay_at_product_page'
    ) {
      if (typeof window.CANDYRACK_CUSTOM_UPGRADE_HANDLE === 'function') {
        window.CANDYRACK_CUSTOM_UPGRADE_HANDLE();
      } else if (window.ajaxCart) {
        window.ajaxCart.load();
      }
      this.resetAfterClose();
      return;
    }

    if (
      (action === 'theme_settings' &&
        window.UPGRADE_OFFER_REDIRECT_TO_CHECKOUT) ||
      action === 'redirect_to_checkout'
    ) {
      redirect('redirect_to_checkout');
    } else if (action === 'redirect_to_cart') {
      redirect('redirect_to_cart');
    }
  };

  resetAfterClose = () => {
    const { resetParentProductVariantId } = this.props;
    // This value is used to show correct image when upgrading
    resetParentProductVariantId();

    // Allow popup to be open again after 2 seconds
    setTimeout(() => {
      window.CandyRack.popupOpen = false;
      this.setState({
        offers: [],
      });
    }, 2000);
  };

  handleClose = async (
    ignoreUpgrade: boolean = false,
    closeAction?: ButtonAction,
  ) => {
    const { settings } = this.state;
    const { upgradeOffer } = this.props;
    const isUpgrade = upgradeOffer && !ignoreUpgrade;
    const action: ButtonAction = closeAction || settings.buttons.close.action;

    logInfo('handleClose', REGISTERED_APPS.ADD_TO_CART, `action ${action}`);
    if (isUpgrade) {
      void this._handleUpgradeOffer(action);
    } else {
      void this._finalizeDialogAction(action);
    }
    if (!window.CANDYRACK_DISABLE_BODY_STYLES) {
      toggleBodyStyle(false);
    }
  };

  _handleUpgradeOffer = async (action: ButtonAction) => {
    const { upgradeOffer } = this.props;
    if (upgradeOffer) {
      try {
        logInfo('handleUpgradeOffer', REGISTERED_APPS.ADD_TO_CART, { action });
        await this.upgradeOffer(upgradeOffer, action);
        logInfo(
          'handleUpgradeOffer',
          REGISTERED_APPS.ADD_TO_CART,
          'dispatching candyrack-closed',
        );
        window?.CandyRackSliderCart?.openCart();
        document.dispatchEvent(new CustomEvent('candyrack-closed'));
      } catch (error) {
        captureException(error);
      }
    }
  };

  _finalizeDialogAction = async (action: ButtonAction) => {
    const { button } = this.state;
    this.setState({ modalOpen: false });

    logInfo(
      'finalizeDialogAction',
      REGISTERED_APPS.ADD_TO_CART,
      'dispatching candyrack-closed',
    );
    document.dispatchEvent(new CustomEvent('candyrack-closed'));

    if (action !== 'theme_settings') {
      void this._handleAction(action);
      this.resetAfterClose();
      return;
    }
    this.resetAfterClose();
    clickHiddenButton(button);
  };

  handleContinue = async () => {
    const {
      settings: {
        buttons: {
          continue_to: { action },
        },
      },
    } = this.state;
    const { upgradeOffer } = this.props;
    if (upgradeOffer) {
      void this._handleUpgradeOffer(action);
    } else {
      this._finalizeDialogAction(action);
    }
    if (!window.CANDYRACK_DISABLE_BODY_STYLES) {
      toggleBodyStyle(false);
    }
  };

  handleReject = async () => {
    const {
      settings: {
        buttons: {
          reject: { action },
        },
      },
    } = this.state;

    logInfo('handleReject', REGISTERED_APPS.ADD_TO_CART, `action ${action}`);
    this.setState({ rejectInProgress: true });
    let frame = document.querySelector('#candyrack-frame') as HTMLIFrameElement;
    const frameDoc = frame.contentDocument;
    this.props.resetUpgradeOffer();
    const nodesForRemoval =
      frameDoc?.querySelectorAll<HTMLElement>(
        '[data-accepted-product-variant]',
      ) || [];

    const bucket: any = [];
    const final = () => {
      this.setState({ rejectInProgress: false });
      this._finalizeDialogAction(action);
    };

    nodesForRemoval.forEach((node: HTMLElement) => {
      const cb = (index: number) => {
        const acceptedProductVariantID = node.dataset.acceptedProductVariant;
        if (acceptedProductVariantID) {
          Promise.resolve(removeFromCart(acceptedProductVariantID)).then(() => {
            if (!bucket[index + 1]) {
              final();
            } else {
              bucket[index + 1](index + 1);
            }
          });
        }
      };
      bucket.push(cb);
    });

    if (bucket.length > 0) {
      bucket[0](0);
    } else {
      final();
    }
    if (!window.CANDYRACK_DISABLE_BODY_STYLES) {
      toggleBodyStyle(false);
    }
  };

  handleAddToCart = async (
    offer: Offer,
    product: Product,
    action: AddButtonAction,
  ) => {
    const { parentProduct } = this.props;
    const { setUpgradeOffer } = this.props;

    if (!parentProduct) {
      console.error(parentProduct);
      throw new Error('parentProduct is undefined');
    }

    if (offer.is_upgrade) {
      setUpgradeOffer(offer);
      return true;
    }
    return await this.handleAddToCartForOffer(offer, parentProduct, product);
  };

  private async handleAddToCartForOffer(
    offer: Offer,
    parentProduct: Product,
    product: Product,
  ) {
    const { parentProductVariantId } = this.props;
    logInfo(
      'Clicked on Add to Cart for normal (non upgrade) offer',
      REGISTERED_APPS.ADD_TO_CART,
    );
    try {
      const properties = offer.noteComputedInFE
        ? { note: offer.noteComputedInFE }
        : undefined;
      const response = await addToCart(
        offer.shopifyProductVariantIdComputedInFE,
        offer.quantityComputedInFE,
        properties,
      );

      saveAssociatedProduct(
        offer,
        parentProduct.handle,
        parentProductVariantId,
      );
      updateDiscounts(REGISTERED_APPS.ADD_TO_CART);

      if (response?.status === 422) {
        // TODO: APPS-10087 - We shouldn't depend on hardcoded English messages.
        // This string matching is fragile and will break with translations or message format changes.
        // This will be addressed in https://digismoothie.atlassian.net/browse/APPS-10087
        const [, itemsActuallyAdded] =
          (typeof response.message === 'string'
            ? response.message.match(
                /Only (\d+) items were added to your cart due to availability./,
              )
            : null) ?? [];

        if (itemsActuallyAdded) {
          // Product(s) has been added but not all of them. Update the quantity to the actual number of items added.
          offer.quantityComputedInFE = parseInt(itemsActuallyAdded, 10);
        } else {
          logInfo(response.description, REGISTERED_APPS.ADD_TO_CART);
          return false;
        }
      }

      // Only track if add to cart was completely successful (no status field = success)
      // Don't track on errors (422, 403, 500, etc.)
      const addedSuccessfully = shouldTrackAddToCart(response);
      if (addedSuccessfully) {
        await trackAddToCart(offer, parentProduct!, product?.id);
      }
      logInfo('dispatching candyrack-offer-added', REGISTERED_APPS.ADD_TO_CART);
      document.dispatchEvent(
        new CustomEvent('candyrack-offer-added', {
          detail: { offer },
        }),
      );
    } catch (error) {
      captureException(error);
    }
    return true;
  }

  handleRemoveFromCart = async (offer: Offer) => {
    const { upgradeOffer, resetUpgradeOffer } = this.props;
    if (offer.discount_code) {
      removeCandyRackAddToCartDiscount(
        offer.discount_code,
        offer.shopifyProductVariantIdComputedInFE,
      );
    }
    if (upgradeOffer && offer.id === upgradeOffer.id) {
      resetUpgradeOffer();

      return;
    }
    try {
      await removeFromCart(offer.shopifyProductVariantIdComputedInFE);
      trackRemoveFromCart(offer);
      logInfo(
        'dispatching candyrack-offer-removed',
        REGISTERED_APPS.ADD_TO_CART,
      );
      document.dispatchEvent(
        new CustomEvent('candyrack-offer-removed', {
          detail: { offer },
        }),
      );
    } catch (error) {
      captureException(error);
    }
  };

  render() {
    return <AppRenderer app={this} />;
  }
}

// Functional component to use hooks
const AppRenderer = ({ app }: { app: App }) => {
  return (
    <Boundary
      onError={() => {
        window.CandyRack.popupOpen = true;
      }}
      app={REGISTERED_APPS.ADD_TO_CART}
    >
      <Suspense fallback={<div />}>
        {app.state?.settings ? (
          <Popup
            {...app.state}
            handleReject={app.handleReject}
            handleClose={app.handleClose}
            handleContinue={app.handleContinue}
            handleAddToCart={app.handleAddToCart}
            handleRemoveFromCart={app.handleRemoveFromCart}
            parentProduct={app.props.parentProduct}
            currentApp={REGISTERED_APPS.ADD_TO_CART}
            rejectInProgress={app.state.rejectInProgress}
          />
        ) : null}
      </Suspense>
    </Boundary>
  );
};

const CandyRackApp = ({
  productHandle,
  appsInfo,
  useDSRecommEngine,
}: Props) => (
  <SharedContextProvider
    currentAppName={REGISTERED_APPS.ADD_TO_CART}
    useDSRecommEngine={useDSRecommEngine}
  >
    <AppsInfoProvider appsInfo={appsInfo}>
      <CurrencyContextProvider>
        <ProductsContextProvider
          storefrontAccessToken={appsInfo.storefront_access_token}
        >
          <ProductsContextConsumer>
            {({ removeOffersWithUnavailableProducts }) => (
              <CandyRackContextProvider
                storefrontAccessToken={appsInfo.storefront_access_token}
                useDSRecommEngine={useDSRecommEngine}
              >
                <CandyRackContextConsumer>
                  {({
                    upgradeOffer,
                    setUpgradeOffer,
                    resetUpgradeOffer,
                    parentProduct,
                    setParentProduct,
                    parentProductVariantId,
                    setParentProductVariantId,
                    resetParentProductVariantId,
                    removeOffersWithProductAlreadyInCart,
                    quantity,
                    setQuantity,
                  }) => (
                    <App
                      productHandle={productHandle}
                      appsInfo={appsInfo}
                      useDSRecommEngine={useDSRecommEngine}
                      removeOffersWithUnavailableProducts={
                        removeOffersWithUnavailableProducts
                      }
                      removeOffersWithProductAlreadyInCart={
                        removeOffersWithProductAlreadyInCart
                      }
                      upgradeOffer={upgradeOffer}
                      setUpgradeOffer={setUpgradeOffer}
                      resetUpgradeOffer={resetUpgradeOffer}
                      parentProduct={parentProduct}
                      setParentProduct={setParentProduct}
                      parentProductVariantId={parentProductVariantId}
                      setParentProductVariantId={setParentProductVariantId}
                      resetParentProductVariantId={resetParentProductVariantId}
                      parentQuantity={quantity}
                      setParentQuantity={setQuantity}
                    />
                  )}
                </CandyRackContextConsumer>
              </CandyRackContextProvider>
            )}
          </ProductsContextConsumer>
        </ProductsContextProvider>
      </CurrencyContextProvider>
    </AppsInfoProvider>
  </SharedContextProvider>
);

export default CandyRackApp;
